import { defineStore } from 'pinia'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'

import AuthService from '../services/auth'
import EditorSettingsService from '../services/editorSettings'
import BackupService from '../services/backup'
import { useUserProfileStore } from './userProfile'
import { useDonationPromptStore } from './donationPrompt'
import { useNotificationsStore } from './notifications'
import { clearSentryUser, syncSentryUserFromAccessToken } from '../utils/sentryUser'
import { startEmailVerificationCooldown } from '../utils/emailVerificationCooldown'
import type { AuthSessionInfo } from '@/types/impersonation'
import {
  clearStoredAdminTokens,
  endImpersonationSession,
  isImpersonationAccessToken,
  hasStoredAdminTokens,
  restoreAdminTokensFromImpersonation,
  saveAdminTokensForImpersonation,
} from '../utils/impersonationSession'
import { clearUserLocalStorage } from '../utils/userLocalStorage'
import { resetLocalEditorSettingsToDefaults } from '../composables/useEditorSettings'
import { useCurrentChapterStore } from './currentChapter'
import {
  beginSessionEnd,
  endSessionEnd,
} from '../utils/sessionLifecycle'
import {
  releaseAuthBlockedChapterBackups,
  resetChapterPersistenceSession,
} from '../services/chapterPersistenceOrchestrator'

interface AuthState {
  accessToken: string
  refreshToken: string
  sessionInfo: AuthSessionInfo | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    accessToken: localStorage.getItem('accessToken') || '',
    refreshToken: localStorage.getItem('refreshToken') || '',
    sessionInfo: null,
  }),

  getters: {
    isLoggedIn: (state) => !!state.accessToken,
    currentUser: (state) => {
      if (state.accessToken === '') return null
      return jwtDecode(state.accessToken)
    },
    emailVerified: (state) => {
      if (!state.accessToken) return false

      try {
        const decoded = jwtDecode(state.accessToken) as any
        return decoded.emailVerified === true
      } catch (error) {
        console.error('Error decoding token:', error)
        return false
      }
    },
    userType: (state) => {
      if (!state.accessToken) {
        return 'non-authenticated'
      }

      try {
        const decoded = jwtDecode(state.accessToken) as any
        return (
          (
            {
              user: 'registered',
              registered: 'registered',
              guest: 'guest',
            } as Record<string, string>
          )[decoded.type] || 'guest'
        )
      } catch (error) {
        console.error('Error decoding token:', error)
        return 'non-authenticated'
      }
    },
    publicAlias: (state) => {
      if (!state.accessToken) return ''

      try {
        const decoded = jwtDecode(state.accessToken) as { publicAlias?: string }
        return decoded.publicAlias || ''
      } catch (error) {
        console.error('Error decoding token:', error)
        return ''
      }
    },
    hasPublicAlias(): boolean {
      return !!this.publicAlias
    },
    isImpersonating(): boolean {
      if (this.sessionInfo?.impersonation?.active) {
        return true
      }
      return isImpersonationAccessToken(this.accessToken)
    },
    isAdmin(): boolean {
      return this.sessionInfo?.isAdmin === true
    },
  },

  actions: {
    setAccessToken(token: string, options: { treatAsLogin?: boolean } = {}) {
      localStorage.setItem('accessToken', token)
      this.accessToken = token
      axios.defaults.headers.common.Authorization = `Bearer ${token}`
      syncSentryUserFromAccessToken(token)
      endSessionEnd()
      try {
        const { userId } = jwtDecode<{ userId?: string }>(token)
        releaseAuthBlockedChapterBackups(userId, options)
      } catch {
        releaseAuthBlockedChapterBackups(undefined, options)
      }
    },

    setRefreshToken(token: string) {
      localStorage.setItem('refreshToken', token)
      this.refreshToken = token
    },

    retagGuestBackupsAfterAuth(newAccessToken: string) {
      const previousToken = this.accessToken
      if (!previousToken) return

      try {
        const previous = jwtDecode<{ userId?: string; type?: string }>(previousToken)
        if (previous.type !== 'guest' || !previous.userId) return
        const next = jwtDecode<{ userId?: string }>(newAccessToken)
        if (!next.userId) return
        BackupService.retagBackupsForUser(previous.userId, next.userId)
      } catch (error) {
        console.error('Error retagging guest chapter backups', error)
      }
    },

    async guest() {
      const response = await AuthService.guest()
      const { access, refresh } = response.data
      this.setAccessToken(access, { treatAsLogin: true })
      this.setRefreshToken(refresh)
    },

    async register(params: {
      name: string
      email: string
      password: string
      turnstileToken?: string
    }) {
      const response = await AuthService.register(params)
      const { access, refresh } = response.data
      this.retagGuestBackupsAfterAuth(access)
      this.setAccessToken(access, { treatAsLogin: true })
      this.setRefreshToken(refresh)

      // The backend already sent the confirmation email, so the resend starts on cooldown
      startEmailVerificationCooldown()

      // Migrate guest settings to backend after successful registration
      await EditorSettingsService.migrateGuestSettingsToBackend()
      await useDonationPromptStore().syncToBackendIfNeeded()

      return response
    },

    async login(params: { email: string; password: string; restoreAccount?: boolean }) {
      const response = await AuthService.login(params)
      const { access, refresh } = response.data
      this.retagGuestBackupsAfterAuth(access)
      this.setAccessToken(access, { treatAsLogin: true })
      this.setRefreshToken(refresh)
      clearStoredAdminTokens()
      await this.fetchSessionInfo()

      // Migrate guest settings to backend after successful login
      await EditorSettingsService.migrateGuestSettingsToBackend()
      await useDonationPromptStore().syncToBackendIfNeeded()

      return response
    },

    async requestAccountDeletion(params: { reason: string }) {
      const response = await AuthService.requestAccountDeletion(params)
      await this.logout({ revokeRemote: false, force: true })
      window.location.href = '/'
      return response
    },

    async initiatePasswordRecovery(params: { email: string }) {
      const response = await AuthService.initiatePasswordRecovery(params)
      return response
    },

    async verifyPasswordRecoveryToken(params: { token: string; password: string }) {
      const response = await AuthService.verifyPasswordRecoveryToken(params)
      const { access, refresh } = response.data
      this.setAccessToken(access, { treatAsLogin: true })
      this.setRefreshToken(refresh)
      return response
    },

    async sendEmailVerification() {
      return AuthService.sendEmailVerification()
    },

    async verifyEmail(params: { token: string }) {
      return AuthService.verifyEmail(params)
    },

    async cancelEmailChange() {
      return AuthService.cancelEmailChange()
    },

    async patch(params: {
      name?: string
      email?: string
      password?: string
      currentPassword?: string
    }) {
      const response = await AuthService.patch(params)
      const { access, refresh } = response.data
      this.setAccessToken(access)
      this.setRefreshToken(refresh)
      return response
    },

    async patchPublicAlias(params: { publicAlias: string }) {
      const response = await AuthService.patchPublicAlias(params)
      const { access, refresh } = response.data
      this.setAccessToken(access)
      this.setRefreshToken(refresh)
      return response
    },

    async lookupPublicAliases(params: { query: string }) {
      return AuthService.lookupPublicAliases(params)
    },

    async fetchSessionInfo() {
      if (!this.accessToken || this.userType !== 'registered') {
        this.sessionInfo = null
        return null
      }

      try {
        const response = await AuthService.me()
        this.sessionInfo = response.data
        return response.data
      } catch {
        this.sessionInfo = null
        return null
      }
    },

    async startImpersonation(params: {
      targetUserId: string
      secret: string
    }) {
      saveAdminTokensForImpersonation()
      const response = await AuthService.impersonate(params)
      localStorage.removeItem('refreshToken')
      this.refreshToken = ''
      this.setAccessToken(response.data.access)
      await this.fetchSessionInfo()
      return response
    },

    async stopImpersonation() {
      beginSessionEnd()

      if (!hasStoredAdminTokens()) {
        await this.logout({ revokeRemote: false, force: true })
        endImpersonationSession()
        return
      }

      restoreAdminTokensFromImpersonation()
      this.accessToken = localStorage.getItem('accessToken') || ''
      this.refreshToken = localStorage.getItem('refreshToken') || ''
      axios.defaults.headers.common.Authorization = `Bearer ${this.accessToken}`
      syncSentryUserFromAccessToken(this.accessToken)
      endSessionEnd()
      this.sessionInfo = null
      await this.fetchSessionInfo()
      window.location.href = '/admin'
    },

    async refresh() {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/v1/auth/refresh`, {
        refreshToken: localStorage.getItem('refreshToken'),
      })
      const { access, refresh } = response.data.data
      this.setAccessToken(access)
      this.setRefreshToken(refresh)
    },

    async logout(options: { revokeRemote?: boolean; force?: boolean } = {}): Promise<boolean> {
      const { revokeRemote = true, force = false } = options
      const chapterStore = useCurrentChapterStore()

      const confirmed = await chapterStore.confirmDiscardUnsyncedChangesBeforeLogout({
        force,
        timeoutMs: 5_000,
      })
      if (!confirmed) {
        return false
      }

      if (revokeRemote && this.accessToken && !this.isImpersonating) {
        try {
          await AuthService.logout()
        } catch (error) {
          console.error('Remote logout failed', error)
        }
      }

      const logoutUserId = (this.currentUser as { userId?: string } | null)?.userId

      beginSessionEnd()
      resetChapterPersistenceSession()
      if (logoutUserId) {
        await BackupService.whenReady()
        BackupService.pruneChapterBackupsOnManualLogout(logoutUserId)
      }
      this.accessToken = ''
      this.refreshToken = ''
      this.sessionInfo = null
      clearUserLocalStorage({ keepChapterBackups: true })
      resetLocalEditorSettingsToDefaults()
      clearStoredAdminTokens()
      delete axios.defaults.headers.common.Authorization
      useUserProfileStore().clear()
      useNotificationsStore().reset()
      clearSentryUser()
      return true
    },
  },
})
